import { postsApi } from '../api/posts';
import { readFeedCache, writeFeedCache, deleteFeedCache, removeLegacyHomeFeedCache } from './homeFeedStorage';

export const FEED_TYPES = { all: null, blogs: 'STANDARD', videos: 'VIDEO', polls: 'POLL', quizes: 'QUIZ', newspapers: 'ARTICLE', winners: 'WINNERS' };
const PAGE_SIZE = 25;
const sessions = new Map();
const blank = () => ({ items: [], featured: [], nextBefore: null, hasMore: false, loaded: false, loading: false, error: '', updatedAt: null });
const cleanItems = (items, type) => items.filter(post => post && post.id != null && !post.hiddenFromFeed && !post.moderationBlocked && !post.blocked && post.allowView !== false && (!post.status || post.status === 'PUBLISHED') && (!post.visibility || post.visibility === 'PUBLIC') && (!type || post.type === type));
const uniquePosts = items => [...new Map(items.map(post => [String(post.id), post])).values()];
const featuredPosts = items => uniquePosts(cleanItems(items).filter(post => post.advertisement)).slice(0, 8);

// One store per account per JS session. Remounts and tab switches subscribe to
// the same store; reloading the app creates a new store and refreshes all heads.
export function createFeedSession(accountId) {
  let state = { hydrated: false, online: navigator.onLine !== false, feeds: Object.fromEntries(Object.keys(FEED_TYPES).map((key) => [key, blank()])) };
  let durable = {};
  let started;
  let refreshing;
  let refreshAgain = false;
  let generation = 0;
  let disposed = false;
  let writes = Promise.resolve();
  const listeners = new Set();
  const requests = new Map();
  const removedIds = new Set();
  const blockedAuthors = new Set();
  const patches = new Map();
  const savedPatches = new Map();
  const authorPatches = new Map();
  let authorRevision = 0;
  const applyLocalChanges = (post, readAuthorRevision = authorRevision) => {
    if (!post || removedIds.has(String(post.id)) || blockedAuthors.has(String(post.author?.id))) return null;
    const patch = patches.get(String(post.id));
    let updated = patch ? { ...post, ...patch } : post;
    if (savedPatches.has(String(post.id))) updated = { ...updated, engagement: { ...updated.engagement, savedByMe: savedPatches.get(String(post.id)) } };
    const authorPatch = authorPatches.get(String(updated.author?.id));
    return authorPatch && authorPatch.revision > readAuthorRevision
      ? { ...updated, author: { ...updated.author, ...authorPatch.patch } } : updated;
  };
  const publish = (patch) => {
    if (disposed) return;
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };
  const updateTab = (key, patch) => publish({ feeds: { ...state.feeds, [key]: { ...state.feeds[key], ...patch } } });
  const persist = () => {
    const snapshot = durable;
    writes = writes.then(() => disposed ? undefined : writeFeedCache(accountId, snapshot));
  };
  const load = async (key, reset = false) => {
    if (disposed || !state.online || !Object.hasOwn(FEED_TYPES, key)) return;
    if (requests.has(key)) return requests.get(key).promise;
    const current = state.feeds[key];
    if (!reset && (!current.loaded || !current.hasMore || current.error)) return;
    const token = {};
    const readAuthorRevision = authorRevision;
    requests.set(key, token);
    updateTab(key, { loading: true, error: '' });
    token.promise = (async () => {
      try {
        const page = await postsApi.feed({ type: FEED_TYPES[key] || undefined, before: reset ? undefined : current.nextBefore, limit: PAGE_SIZE });
        if (disposed || requests.get(key) !== token) return;
        if (!page || !Array.isArray(page.items)) throw new Error('Invalid feed response');
        const received = page.items.map(post => applyLocalChanges(post, readAuthorRevision)).filter(Boolean);
        const restoredPromotions = state.feeds[key].items.filter(post => patches.get(String(post.id))?.advertisement === false);
        const items = cleanItems(uniquePosts([...received, ...restoredPromotions]), FEED_TYPES[key]).filter(post => !post.advertisement);
        const merged = reset ? items : [...state.feeds[key].items, ...items];
        const unique = [...new Map(merged.map((post) => [String(post.id), post])).values()];
        const head = { items: unique, nextBefore: page.nextBefore || null, hasMore: Boolean(page.hasMore), loaded: true, updatedAt: Date.now() };
        if (key === 'all' && reset) {
          // Banners share the Home response and cache. Local moderation wins
          // over a request that began before the promotion or its cancellation.
          head.featured = featuredPosts([
            ...(Array.isArray(page.featured) ? page.featured : []),
            ...received.filter(post => post.advertisement),
            ...state.feeds.all.featured.filter(post => patches.has(String(post.id))),
          ].map(post => applyLocalChanges(post, readAuthorRevision)).filter(Boolean));
        }
        updateTab(key, head);
        // Persist a bounded first page for every tab. Older pages remain in
        // session memory; the saved cursor always belongs to the saved items.
        if (reset) { durable = { ...durable, [key]: head }; persist(); }
      } catch (error) {
        if (!disposed && requests.get(key) === token) updateTab(key, { loaded: true, error: error.message || 'Unable to refresh posts' });
      } finally {
        if (requests.get(key) === token) { requests.delete(key); updateTab(key, { loading: false }); }
      }
    })();
    return token.promise;
  };
  const refresh = () => {
    if (disposed || !state.online) return Promise.resolve();
    if (refreshing) return refreshing;
    const activeGeneration = generation;
    // Winner archives are less frequently visited; fetch on first use, then keep the same session cache.
    const queue = Object.keys(FEED_TYPES).filter(key => key !== 'winners' || state.feeds.winners.loaded);
    const worker = async () => {
      while (queue.length && !disposed && state.online && generation === activeGeneration) await load(queue.shift(), true);
    };
    // Home and Posts start first; two workers avoid a six-request burst.
    const running = Promise.all([worker(), worker()]).finally(() => {
      if (refreshing !== running) return;
      refreshing = null;
      if (refreshAgain) { refreshAgain = false; void refresh(); }
    });
    refreshing = running;
    return refreshing;
  };
  const start = () => {
    if (started) return started;
    started = (async () => {
      void removeLegacyHomeFeedCache();
      const saved = await readFeedCache(accountId);
      if (disposed) return;
      const feeds = { ...state.feeds };
      if (saved?.version === 1 && saved.feeds) {
        for (const [key, type] of Object.entries(FEED_TYPES)) {
          const cached = saved.feeds[key];
          if (!cached || !Array.isArray(cached.items) || !Number.isFinite(cached.updatedAt)) continue;
          const restored = cached.items.slice(0, PAGE_SIZE).map(post => applyLocalChanges(post, -1)).filter(Boolean);
          durable[key] = { ...blank(), ...cached, items: cleanItems(restored, type).filter(post => !post.advertisement), featured: key === 'all' ? featuredPosts([...(Array.isArray(cached.featured) ? cached.featured : []), ...restored].map(post => applyLocalChanges(post, -1)).filter(Boolean)) : [], loading: false, error: '', loaded: true };
          feeds[key] = durable[key];
        }
      }
      publish({ hydrated: true, feeds });
      if (savedPatches.size && Object.keys(durable).length) persist();
      await refresh();
    })();
    return started;
  };
  const mutate = (mapper, upsert = null) => {
    // Keep requests alive: applyLocalChanges also filters their responses, so
    // edits/deletes survive without interrupting startup of the remaining tabs.
    const mapFeeds = feeds => Object.fromEntries(Object.entries(feeds).map(([key, feed]) => {
      const candidates = uniquePosts([...feed.items, ...(key === 'all' ? feed.featured || [] : []), ...(upsert && feed.loaded ? [upsert] : [])].map(post => mapper(post)).filter(Boolean));
      const items = cleanItems(candidates, FEED_TYPES[key]).filter(post => !post.advertisement);
      if (upsert) items.sort((a, b) => Date.parse(b.publishedAt || b.createdAt || 0) - Date.parse(a.publishedAt || a.createdAt || 0));
      return [key, { ...feed, items, featured: key === 'all' ? featuredPosts(candidates) : [] }];
    }));
    durable = mapFeeds(durable);
    publish({ feeds: mapFeeds(state.feeds) });
    persist();
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    start, refresh, load,
    suspend() {
      // Account switching must stop queued work before credentials change.
      // Reject late responses too, while keeping this account's saved posts.
      generation += 1;
      requests.clear();
      refreshing = null;
      refreshAgain = false;
      publish({ online: false, feeds: Object.fromEntries(Object.entries(state.feeds).map(([key, feed]) => [key, { ...feed, loading: false }])) });
    },
    invalidate() { if (refreshing) refreshAgain = true; else void refresh(); },
    setOnline(online) {
      const changed = state.online !== online;
      publish({ online });
      if (online && changed && state.hydrated) {
        if (refreshing) refreshAgain = true;
        else void refresh();
      }
    },
    removePost(id) { removedIds.add(String(id)); mutate(applyLocalChanges); },
    getSavedState(id, fallback = false) { return savedPatches.get(String(id)) ?? Boolean(fallback); },
    setSavedState(id, saved) {
      savedPatches.set(String(id), Boolean(saved));
      // A direct collection/profile visit may save before the feed has loaded.
      // Keep the existing offline cache until start() can merge this change.
      if (state.hydrated) mutate(applyLocalChanges);
      else publish({});
    },
    removeAuthor(id) { blockedAuthors.add(String(id)); mutate(applyLocalChanges); },
    updateAuthor(id, patch) {
      authorPatches.set(String(id), { patch, revision: ++authorRevision });
      mutate(post => String(post.author?.id) === String(id) ? { ...post, author: { ...post.author, ...patch } } : post);
    },
    updatePost(id, patch) {
      const existing = Object.values(state.feeds).flatMap(feed => [...feed.items, ...feed.featured]).find(post => String(post.id) === String(id));
      const normalized = patch.__backend ? { ...patch, type: patch.type === 'post' ? 'STANDARD' : patch.type?.toUpperCase() } : patch;
      patches.set(String(id), { ...patches.get(String(id)), ...normalized });
      const changedPlacement = Object.hasOwn(patch, 'advertisement');
      const candidate = existing || (patch.id && patch.author && patch.type ? normalized : null);
      mutate(applyLocalChanges, changedPlacement && candidate ? candidate : null);
    },
    async clear() {
      disposed = true;
      requests.clear();
      listeners.clear();
      await writes;
      await deleteFeedCache(accountId);
    },
  };
}

export function getFeedSession(accountId) {
  if (!sessions.has(accountId)) sessions.set(accountId, createFeedSession(accountId));
  return sessions.get(accountId);
}
export async function clearFeedSession(accountId) {
  const session = sessions.get(accountId);
  sessions.delete(accountId);
  if (session) await session.clear();
  else await deleteFeedCache(accountId);
}
export function suspendFeedSession(accountId) {
  sessions.get(accountId)?.suspend();
}

// Keep already loaded feeds consistent after moderation, without refetching all tabs.
export function updateCachedAuthor(userId, patch) {
  sessions.forEach(session => session.updateAuthor(userId, patch));
}
